import { FC, useEffect, useRef, useState } from "react";

// * Props
import TranscodePresetsPopupProps, { ViewModes } from "./TranscodePresetsPopup.interface";

// * Hooks
import {
  RequestPaginationParams,
  TranscodePreset,
  useCreateTranscodePresetMutation,
  useDeleteTranscodePresetMutation,
  useGetTranscodePresetsQuery,
  useUpdateTranscodePresetMutation
} from "../../features/api/apiSlice";
import { useForm } from "react-hook-form";
import useCountItemsInBlock from "../../hooks/useCountItemsInBlock";

// * Components
import { Popup } from "../Popup";
import PopupStructure from "../PopupStructure";
import { PopupTitle } from "../PopupTitle";
import ConfirmationPopup from "../ConfirmationPopup";
import TranscodePresetsPopupContent from "./TranscodePresetsPopupContent";
import TranscodePresetsPopupFooter from "./TranscodePresetsPopupFooter";

// * Helpers
import {
  TABLE_ROW_GAP_HEIGHT,
} from "./TranscodePresetsPopup.data";

// styles
import styles from "./TranscodePresetsPopup.module.css";

const TranscodePresetsPopup: FC<TranscodePresetsPopupProps> = ({ handleSelectPreset, selectedTranscodePreset, ...popupProps }) => {
  const tableRowRef = useRef<HTMLTableRowElement>(null);
  const [createTranscodePreset, { isLoading: isCreating }] = useCreateTranscodePresetMutation();
  const [updateTranscodePreset, { isLoading: isUpdating }] = useUpdateTranscodePresetMutation();
  const [deleteTranscodePreset, { isLoading: isDeleting }] = useDeleteTranscodePresetMutation();
  const [viewMode, setViewMode] = useState<ViewModes>(ViewModes.PRESETS_LIST);
  const [transcodePresetToDelete, setTranscodePresetToDelete] = useState<null | TranscodePreset>(null);
  const [transcodePresetToUpdate, setTranscodePresetToUpdate] = useState<null | TranscodePreset>(null);
  const {
    ref: popupStructureRef,
    maxItemCount
  } = useCountItemsInBlock({
    itemRef: tableRowRef,
    gapHeight: TABLE_ROW_GAP_HEIGHT,
    minCount: 5
  });
  const [currentPage, setCurrentPage] = useState(1);
  const paginationParams: RequestPaginationParams = { page: currentPage, per_page: maxItemCount };
  const { data: transcodePresets, isSuccess: isTranscodePresets } = useGetTranscodePresetsQuery(paginationParams);

  const {
    reset,
    watch,
    setValue
  } = useForm<{ selectedTranscodePreset: TranscodePreset }>();

  const handleCreate = (createParams: Partial<TranscodePreset>) => {
    createTranscodePreset({ ...createParams })
      .then(() => {
        setViewMode(ViewModes.PRESETS_LIST);
      });
  };

  const handleUpdate = (updateParams: Partial<TranscodePreset>) => {
    if (!transcodePresetToUpdate) return;

    const id = transcodePresetToUpdate.id;
    updateTranscodePreset({ ...updateParams, id })
      .then(() => {
        setViewMode(ViewModes.PRESETS_LIST);
      });
  };

  const getSubmitHandler = () => {
    if (viewMode === ViewModes["CREATE_PRESET"]) {
      return handleCreate;
    } else if (viewMode === ViewModes["EDIT_PRESET"]) {
      return handleUpdate;
    }
  };

  const changeSelectedTranscodePreset = (transcodePresetId: number) => {
    const transcodePreset = transcodePresets?.transcode_presets.find(({ id }) => id === transcodePresetId);

    if (transcodePreset) {
      setValue("selectedTranscodePreset", transcodePreset);
    }
  };

  const handleSelect = () => {
    const value = watch("selectedTranscodePreset");

    handleSelectPreset?.(value);
    popupProps.onClose();
  };

  const handleTranscodePresetToUpdate = (transcodePreset: TranscodePreset) => {
    setTranscodePresetToUpdate(transcodePreset);
    setViewMode(ViewModes.EDIT_PRESET);
  };

  const handleOpenDeleteConfirmation = () => {
    if (!transcodePresetToDelete) return;

    deleteTranscodePreset({ id: transcodePresetToDelete.id })
      .then(() => {
        setTranscodePresetToDelete(null);
      });
  };

  const handleReset = () => {
    reset();
    handleSelectPreset?.(undefined);
  };

  useEffect(() => {
    if (!selectedTranscodePreset) return;

    setValue("selectedTranscodePreset", selectedTranscodePreset);
  }, [selectedTranscodePreset]);

  return (
    <>
      {isTranscodePresets &&
        <>
          <Popup {...popupProps} bodyClassName={styles["transcode-presets-popup"]}>
            <PopupStructure
              className={styles["transcode-presets-popup__structure"]}
              containerClassName={styles["transcode-presets-popup__structure-container"]}
              structureRef={popupStructureRef}
              headerComponent={
                <PopupTitle className={styles["transcode-presets-popup__header"]}>Ваши пресеты</PopupTitle>
              }
              contentComponent={
                <TranscodePresetsPopupContent
                  transcodePresetToUpdate={transcodePresetToUpdate}
                  viewMode={viewMode}
                  selectedTranscodePresetId={watch("selectedTranscodePreset")?.id}
                  functionalItemRef={tableRowRef}
                  isShowRadioButtons={Boolean(handleSelectPreset)}
                  paginationParams={paginationParams}
                  onSubmit={getSubmitHandler()}
                  onChange={changeSelectedTranscodePreset}
                  onChangeViewMode={setViewMode}
                  handleTranscodePresetToUpdate={handleTranscodePresetToUpdate}
                  setTranscodePresetToDelete={setTranscodePresetToDelete}
                />
              }
              footerComponent={
                <TranscodePresetsPopupFooter
                  viewMode={viewMode}
                  transcodePresetToUpdate={transcodePresetToUpdate}
                  paginationParams={paginationParams}
                  isShowleftButtons={Boolean(handleSelectPreset)}
                  currentPage={currentPage}
                  isLoading={isCreating ?? isUpdating}
                  onChangeViewMode={setViewMode}
                  onReset={handleReset}
                  onSelect={handleSelect}
                  onClose={popupProps.onClose}
                  setCurrentPage={setCurrentPage}
                />
              }
            />
          </Popup>

          {Boolean(transcodePresetToDelete) &&
            <ConfirmationPopup
              isShow={Boolean(transcodePresetToDelete)}
              label={`Вы уверены, что хотите удалить преcет "${transcodePresetToDelete?.name}"?`}
              action={handleOpenDeleteConfirmation}
              onClose={() => setTranscodePresetToDelete(null)}
              actionButtonLabel={{
                label: isDeleting ? "Удаление..." : "Удалить"
              }}
            />
          }
        </>
      }
    </>
  );
};

export default TranscodePresetsPopup;